import { useMemo, useState } from "react";
import { Combobox, formatCurrency } from "medere-ui-framework";
import {
    ProfessionalActivityBudgetDetailDTO,
    defaultProfessionalActivityBudgetDetailDTO,
    withDefaultCoveragePercentages,
} from "../../../dto/budget/BudgetDetailDTO";
import { NomenclatorDTO } from "../../../dto/NomenclatorDTO";
import { getProfessionalActivitiesWithContract } from "../../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import {
    BILL_ITEM_CATEGORY_CONFIG,
    amountOf,
    getCategorySubtotal,
    getDuplicatedHealthPatientServices,
    getItemWarnings,
    getUnitsLabel,
    newBillableItem,
    recalculateItem,
    sortByHealthPatientService,
    toWholeUnits,
    withUnitPrice,
} from "./billItems";
import { ContractContext, canValueByContract, fetchContractValue, professionalActivityValue } from "./contractRevaluation";
import LoadTable, { LoadTableColumn, LoadTableRow } from "../../../components/novarium/LoadTable";
import NumberField from "./NumberField";
import { AddRowButton, FieldError, RowActions, WarningTags, formatApplicationDate, formatCoverage } from "./tableParts";

type Row = LoadTableRow & { item: ProfessionalActivityBudgetDetailDTO; index: number };

interface ProfessionalActivityTabProps {
    items: ProfessionalActivityBudgetDetailDTO[];
    onChange: (items: ProfessionalActivityBudgetDetailDTO[]) => void;
    onEdit: (index: number) => void;
    contract: ContractContext;
    healthEntity: number | null | undefined;
}

interface AddDraft {
    practice: NomenclatorDTO | null;
    text: string;
    units: number | null;
    price: number | null;
    contractValues: Partial<ProfessionalActivityBudgetDetailDTO> | null;
    /** El valor unitario lo escribió la persona: el del convenio no lo pisa. */
    priceEdited: boolean;
    errors: { practice?: string; units?: string; price?: string };
}

const emptyDraft = (): AddDraft => ({ practice: null, text: '', units: 1, price: null, contractValues: null, priceEdited: false, errors: {} });

const CONFIG = BILL_ITEM_CATEGORY_CONFIG.professionalActivities;

export default function ProfessionalActivityTab({ items, onChange, onEdit, contract, healthEntity }: ProfessionalActivityTabProps) {
    const [draft, setDraft] = useState<AddDraft>(emptyDraft);
    const selectionEnabled = canValueByContract(contract);

    const duplicated = useMemo(() => getDuplicatedHealthPatientServices(items), [items]);
    const rows: Row[] = useMemo(
        () => sortByHealthPatientService(items).map(({ item, index }) => ({ rowKey: `${index}-${item.nomenclator}`, item, index })),
        [items]
    );

    const update = (index: number, item: ProfessionalActivityBudgetDetailDTO) =>
        onChange(items.map((current, i) => i === index ? recalculateItem('professionalActivities', item) : current));

    const selectPractice = async (practice: NomenclatorDTO | null) => {
        if (!practice?.nomenclator) {
            setDraft(prev => ({ ...prev, practice: null, text: practice?.codeanddescription ?? '', contractValues: null }));
            return;
        }
        setDraft(prev => ({ ...prev, practice, text: practice.codeanddescription ?? '', priceEdited: false, errors: { ...prev.errors, practice: undefined } }));
        // Práctica agregada a mano: modalidad internación si la factura tiene internación, si no ambulatoria.
        const value = await fetchContractValue(practice.nomenclator, contract);
        const contractValues = professionalActivityValue(value);
        setDraft(prev => prev.practice?.nomenclator === practice.nomenclator
            ? { ...prev, contractValues, price: prev.priceEdited ? prev.price : contractValues.unitprice ?? 0, errors: { ...prev.errors, price: undefined } }
            : prev);
    };

    const add = () => {
        const errors: AddDraft['errors'] = {};
        if (!draft.practice?.nomenclator) errors.practice = 'Elegí una práctica';
        const units = toWholeUnits(draft.units);
        if (units <= 0) errors.units = 'Ingresá las unidades';
        if (!draft.price || draft.price <= 0) errors.price = 'Ingresá el valor unitario';
        if (errors.practice || errors.units || errors.price) {
            setDraft(prev => ({ ...prev, errors }));
            return;
        }
        const base: ProfessionalActivityBudgetDetailDTO = {
            ...defaultProfessionalActivityBudgetDetailDTO,
            ...draft.contractValues,
            nomenclator: draft.practice!.nomenclator!,
            description: draft.practice!.codeanddescription ?? '',
            unitsqty: units,
            billableitems: [newBillableItem(units)],
        };
        const item = withDefaultCoveragePercentages(withUnitPrice('professionalActivities', base, draft.price!));
        onChange([...items, recalculateItem('professionalActivities', item)]);
        setDraft(emptyDraft());
    };

    const draftTotal = draft.practice && draft.price ? formatCurrency(amountOf(toWholeUnits(draft.units), draft.price).toNumber()) : '—';

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'practice',
            header: CONFIG.itemHeader,
            className: 'nv-col-item',
            addCell: (
                <div className="nv-cell-field">
                    <Combobox
                        getItems={getProfessionalActivitiesWithContract}
                        params={{ healthEntity, serviceDate: contract.date, orderByNomenclatorType: true }}
                        optionLabel="codeanddescription"
                        placeholder={selectionEnabled ? 'Buscá por código o nombre' : 'Elegí obra social, plan y fecha primero'}
                        minPanelWidth={600}
                        maxPanelWidth={800}
                        value={draft.text}
                        setValue={(e: NomenclatorDTO | null) => { void selectPractice(e); }}
                        disable={!selectionEnabled}
                        forceSelection={false}
                        error={Boolean(draft.errors.practice)}
                        caption={draft.errors.practice}
                    />
                </div>
            ),
            body: ({ item }) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        },
        {
            key: 'date',
            header: 'Fecha',
            align: 'center',
            className: 'nv-col-date',
            addCell: <span className="nv-muted">—</span>,
            body: ({ item }) => formatApplicationDate(item.applicationdate),
        },
        {
            key: 'units',
            header: CONFIG.quantityHeader,
            align: 'center',
            className: 'nv-col-qty',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="integer" inCell ariaLabel="Unidades de la práctica nueva" value={draft.units} min={0}
                        invalid={Boolean(draft.errors.units)} describedBy="ap-add-units-error"
                        onCommit={(units) => setDraft(prev => ({ ...prev, units, errors: { ...prev.errors, units: undefined } }))} />
                    <FieldError id="ap-add-units-error" message={draft.errors.units} />
                </div>
            ),
            body: ({ item }) => {
                const units = getUnitsLabel(item);
                return <span className="nv-tip" data-pr-tooltip={`${units.detail}. Se cambian desde Editar práctica.`}>{units.text}</span>;
            },
        },
        {
            key: 'price',
            header: 'Valor unitario',
            align: 'right',
            className: 'nv-col-money',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="currency" inCell ariaLabel="Valor unitario de la práctica nueva" value={draft.price} min={0}
                        invalid={Boolean(draft.errors.price)} describedBy="ap-add-price-error"
                        onCommit={(price) => setDraft(prev => ({ ...prev, price, priceEdited: true, errors: { ...prev.errors, price: undefined } }))} />
                    <FieldError id="ap-add-price-error" message={draft.errors.price} />
                </div>
            ),
            body: ({ item, index }) => (
                <NumberField kind="currency" inCell ariaLabel={`Valor unitario de ${item.description}`} value={item.unitprice} min={0}
                    onCommit={(price) => update(index, withUnitPrice('professionalActivities', item, price))} />
            ),
        },
        {
            key: 'coverage',
            header: 'Cobertura OS',
            align: 'center',
            className: 'nv-col-pct',
            addCell: <span className="nv-muted">{formatCoverage(draft.contractValues?.healthentitypercentagecoverage)}</span>,
            body: ({ item }) => formatCoverage(item.healthentitypercentagecoverage),
        },
        {
            key: 'total',
            header: 'Total',
            align: 'right',
            className: 'nv-col-money',
            addCell: <span className="nv-strong">{draftTotal}</span>,
            body: ({ item }) => <span className="nv-strong">{formatCurrency(item.valuemonth ?? 0)}</span>,
        },
        {
            key: 'status',
            header: 'Estado',
            className: 'nv-col-status',
            body: ({ item }) => <WarningTags warnings={getItemWarnings('professionalActivities', item, duplicated)} />,
        },
    ];

    return (
        <LoadTable
            rows={rows}
            columns={columns}
            addAction={<AddRowButton label="Agregar práctica" onClick={add} />}
            rowActions={({ item, index }) => (
                <RowActions
                    itemName={item.description}
                    editLabel="Editar práctica"
                    onEdit={() => onEdit(index)}
                    onRemove={() => onChange(items.filter((_, i) => i !== index))}
                />
            )}
            emptyMessage={CONFIG.emptyMessage}
            footerValues={{ total: formatCurrency(getCategorySubtotal(items).toNumber()) }}
        />
    );
}
